import { takeLeading, select } from 'redux-saga/effects';
import { get, isNil, isNull } from 'lodash';
import { callApi } from 'helpers/saga-helpers';
import {
  REQUEST_PREOPEN_CLAIM,
  REQUEST_OPEN_CLAIM_MANAGER,
  REQUEST_OPEN_CLAIM_GPO,
  REQUEST_MIGRATE_CLAIM,
  REQUEST_COMPLETE_CLAIM_LAWYER_GPO,
  REQUEST_VALIDATE_COMPLETE_CLAIM,
} from 'containers/RecapPage/constants';
import userToken from 'containers/services/security/access-token-storage';
import { makeSelectKeycloakToken } from 'containers/AppContainer/selectors';
import currentUserStorage from 'containers/services/security/current-user-storage';
import { setClaimId } from 'containers/PreOpeningPage/actions';
import { makeSelectClaimId } from 'containers/PreOpeningPage/selectors';

export function* preopenClaim(action: any): Generator<unknown> {
  const { claimToPreOpen, successCallback } = action;
  const path = '/corporeal/common/preopen-claim';

  const token = get(userToken.get(), 'token')
    ? get(userToken.get(), 'token')
    : yield select(makeSelectKeycloakToken());

  if (isNull(token)) return;

  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      name: get(currentUserStorage?.get(), 'username'),
    },
    body: JSON.stringify({
      claimToPreOpen,
      isLawyer: get(userToken.get(), 'token') ? false : true,
    }),
  };

  const errorResponse = yield callApi(path, {}, options);

  if (!errorResponse) {
    yield successCallback();
  }
}

export function* preopenClaimSaga() {
  yield takeLeading(REQUEST_PREOPEN_CLAIM, preopenClaim);
}

export function* openClaimManager(action: any): Generator<unknown> {
  const { claimToOpen, successCallback } = action;
  const path = '/corporeal/open-claim-manager';

  const token = get(userToken.get(), 'token');

  if (isNull(token)) return;

  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      claimDetails: { ...claimToOpen },
    }),
  };

  const errorResponse = yield callApi(path, {}, options, []);

  if (!errorResponse) {
    yield successCallback();
  }
}

export function* openClaimManagerSaga() {
  yield takeLeading(REQUEST_OPEN_CLAIM_MANAGER, openClaimManager);
}

export function* openClaimGpo(action: any): Generator<unknown> {
  const { claimToOpen, successCallback, failCallBack } = action;
  const path = '/corporeal/open-claim-gpo';

  const token = get(userToken.get(), 'token');

  if (isNull(token)) return;

  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      claimDetails: { ...claimToOpen },
    }),
  };

  const errorResponse = yield callApi(path, {}, options, []);

  if (!errorResponse) {
    yield successCallback();
  } else {
    yield failCallBack();
  }
}
export function* openClaimGpoSaga() {
  yield takeLeading(REQUEST_OPEN_CLAIM_GPO, openClaimGpo);
}
export function* requestMigrateClaim(action: any): Generator<unknown> {
  const { claim, successCallback } = action;
  const path = '/corporeal/migrate-claim';

  const token = get(userToken.get(), 'token', null);

  if (isNil(token)) return;

  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      name: get(currentUserStorage?.get(), 'username'),
    },
    body: JSON.stringify({
      claimToPreOpen: claim,
      isLawyer: false,
    }),
  };

  const errorResponse = yield callApi(path, {}, options, undefined, setClaimId);

  if (!errorResponse) {
    const claimId = yield select(makeSelectClaimId());
    if (claimId) {
      yield successCallback(get(claimId, 'data.claimId'));
    }
  }
}
export function* requestMigrateClaimSaga() {
  yield takeLeading(REQUEST_MIGRATE_CLAIM, requestMigrateClaim);
}

export function* requestCompleteClaimLawyer(action: any): Generator<unknown> {
  const { payload, successCallback } = action;
  const path = '/corporeal/common/complete-claim';

  const token = get(userToken.get(), 'token')
    ? get(userToken.get(), 'token')
    : yield select(makeSelectKeycloakToken());

  if (isNil(token)) return;

  const options = {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      name: get(currentUserStorage?.get(), 'username'),
    },
    body: JSON.stringify({
      details: payload,
      claimId: payload.claim.id,
      isGpo: get(userToken.get(), 'token') ? true : false,
      isLawyer: get(userToken.get(), 'token') ? false : true,
    }),
  };

  const errorResponse = yield callApi(path, {}, options, undefined, setClaimId);

  if (!errorResponse) {
    const claimId = yield select(makeSelectClaimId());
    if (claimId) {
      yield successCallback(get(claimId, 'data.claimId'));
    }
  }
}

export function* requestCompleteClaimLawyerSaga() {
  yield takeLeading(
    REQUEST_COMPLETE_CLAIM_LAWYER_GPO,
    requestCompleteClaimLawyer,
  );
}

export function* requestValidateCompleteClaim(action: any): Generator<unknown> {
  const { payload, successCallback } = action;
  const path = '/corporeal/validate-complete-claim';

  const token = get(userToken.get(), 'token')
    ? get(userToken.get(), 'token')
    : yield select(makeSelectKeycloakToken());

  if (isNil(token)) return;

  const options = {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      name: get(currentUserStorage?.get(), 'username'),
    },
    body: JSON.stringify({
      details: payload,
      claimId: payload.claim.id,
      isGpo: get(userToken.get(), 'token') ? true : false,
      isLawyer: get(userToken.get(), 'token') ? false : true,
    }),
  };

  const errorResponse = yield callApi(path, {}, options, undefined, setClaimId);

  if (!errorResponse) {
    const claimId = yield select(makeSelectClaimId());
    if (claimId) {
      yield successCallback(get(claimId, 'data.claimId'));
    }
  }
}
export function* requestValidateCompleteClaimSaga() {
  yield takeLeading(
    REQUEST_VALIDATE_COMPLETE_CLAIM,
    requestValidateCompleteClaim,
  );
}

export default [
  preopenClaimSaga,
  openClaimManagerSaga,
  openClaimGpoSaga,
  requestMigrateClaimSaga,
  requestCompleteClaimLawyerSaga,
  requestValidateCompleteClaimSaga,
];
